import { createSlice, type PayloadAction, type WritableDraft } from '@reduxjs/toolkit';
import type { AppConfigState } from '@src/providers/AppConfigProvider/AppConfigProvider.types';
import type { BlotterTableFilter, BlotterTableStorageMap, ExtraGridState } from '@talos/kyoko';
import type { GridState } from 'ag-grid-community';
import { cloneDeep } from 'lodash-es';

let blotterActions: ReturnType<typeof createBlotterSlice>['actions'] | undefined;
export const getBlottersSliceActions = () => {
  if (!blotterActions) {
    throw new Error('AppConfigSlice not initialized');
  }
  return blotterActions;
};

/** Reducer for {@link AppConfigReduxState}'s blotters3, providing storage for with usePersistedBlotterTable */
export const createBlotterSlice = (appConfig: AppConfigState) => {
  const result = createSlice({
    name: 'appConfig/blotters',
    initialState: appConfig.blotters3,
    reducers: {
      setGridState: (
        state: WritableDraft<BlotterTableStorageMap>,
        action: PayloadAction<{ blotterID: string; gridState: GridState | undefined }>
      ) => {
        if (!(action.payload.blotterID in state)) {
          state[action.payload.blotterID] = {};
        }
        state[action.payload.blotterID].gridState = action.payload.gridState;
      },
      setFilterState: (
        state: WritableDraft<BlotterTableStorageMap>,
        action: PayloadAction<{ blotterID: string; filter: BlotterTableFilter | undefined }>
      ) => {
        if (!(action.payload.blotterID in state)) {
          state[action.payload.blotterID] = {};
        }
        state[action.payload.blotterID].filter = action.payload.filter;
      },
      setExtraGridState: (
        state: WritableDraft<BlotterTableStorageMap>,
        action: PayloadAction<{ blotterID: string; extraGridState: ExtraGridState | undefined }>
      ) => {
        if (!(action.payload.blotterID in state)) {
          state[action.payload.blotterID] = {};
        }
        state[action.payload.blotterID].extraGridState = action.payload.extraGridState;
      },

      cloneState: (
        state: WritableDraft<BlotterTableStorageMap>,
        action: PayloadAction<{ fromBlotterID: string; toBlotterID: string }>
      ) => {
        // Only perform the cloning if the originating blotter ID actually has something persisted
        if (state[action.payload.fromBlotterID]) {
          state[action.payload.toBlotterID] = cloneDeep(state[action.payload.fromBlotterID]);
        }
      },

      clearAllBlotterState: (state: WritableDraft<BlotterTableStorageMap>) => {
        Object.keys(state).forEach(blotterId => {
          delete state[blotterId];
        });
      },
    },
  });

  blotterActions = result.actions;
  return result;
};
